import {Fragment, useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Grid, TextField, Typography} from '@material-ui/core';
import HighBloodPressure from './HighBloodPressure';
import Cholesterol from './Cholesterol';
import Diabetes from './Diabetes';
import PsychologicalDisorder from './PsychologicalDisorder';
import BoneMuscleJoint from './BoneMuscleJoint';
import HealthRelated from './HealthRelated';
import NervousSystemDisorder from './NervousSystemDisorder';
import RespiratoryDisorder from './RespiratoryDisorder';
import HeartDisease from './HeartDisease';
import CancerOrTumors from './CancerOrTumors';
import Stroke from './Stroke';
import SleepApnea from './SleepApnea';
import DigestiveSystemDisorder from './DigestiveSystemDisorder';
import Others from './Others';
import {Health} from '../../../Core/mobx/ParameterStore/Health';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {useDebounce} from 'use-debounce';
import {CoreHealthQuestions, coreHealthQuestions} from './coreHealthQuestions';
import RootStore from '../../../Core/mobx/RootStore';

type HealthQuestionsPanelProps = {
    parameters: Health;
    changed: (p: Parameter, value: any) => void;
};

export type HealthQuestionsProps = {
    parameters: Health;
    valueChanged: (p: Parameter, value: any) => void;
    counter: (category: string) => JSX.Element | null;
    coreQuestions: {name: string; label: string}[];
};

const healthConditionSections = {
    highBloodPressure: HighBloodPressure,
    cholesterol: Cholesterol,
    diabetes: Diabetes,
    psychologicalDisorder: PsychologicalDisorder,
    bonemuscle: BoneMuscleJoint,
    health: HealthRelated,
    nervousSystemDisorder: NervousSystemDisorder,
    respiratoryDisorder: RespiratoryDisorder,
    heartDisease: HeartDisease,
    cancerOrTumors: CancerOrTumors,
    stroke: Stroke,
    sleepApnea: SleepApnea,
    digestiveSystemDisorder: DigestiveSystemDisorder,
    others: Others,
};

export const HealthQuestionsList = observer(({parameters, changed}: HealthQuestionsPanelProps) => {
    const [searchUndebounced, setSearch] = useState('');
    const [counters, setCounters] = useState({} as any);

    const {loadingClientData} = RootStore.agentPortalStore.parameterStore;

    const [search] = useDebounce(searchUndebounced, 300);

    useEffect(() => {
        const initializeCounters = () => {
            const newCounters: Record<string, number> = {};

            Object.entries(coreHealthQuestions).forEach(([category, questions]) => {
                newCounters[category] = questions.reduce((sum, question) => {
                    if (!!parameters[question.name as keyof typeof parameters]?.value) {
                        return sum + 1;
                    }
                    return sum;
                }, 0);
            });

            setCounters(newCounters);
        };

        initializeCounters();
    }, [loadingClientData]);

    const counterElement = (category: string) => {
        const counter = counters[category];
        if (counter > 0) {
            return <span style={{color: 'green', fontWeight: 'bold'}}>{counter}</span>;
        }
        return null;
    };

    const updateCounter = (category: string, isActive: boolean) => {
        setCounters((prevCounters: any) => ({
            ...prevCounters,
            [category]: (prevCounters[category] || 0) + (isActive ? 1 : -1),
        }));
    };

    const enhancedChanged = (parameter: Parameter & {category?: string}, value: any) => {
        if (parameter.category) {
            const isActive = !!value;
            updateCounter(parameter.category, isActive);
        }
        changed(parameter, value);
    };

    const filteredSections = useMemo(() => {
        if (!search) return coreHealthQuestions;

        return Object.entries(coreHealthQuestions).reduce((acc, [key, questions]) => {
            const filtered = questions.filter(({label}) => label.toLowerCase().includes(search.toLowerCase()));
            if (filtered.length > 0) {
                acc[key] = filtered;
            }
            return acc;
        }, {} as CoreHealthQuestions);
    }, [search]);

    return (
        <>
            <Fragment key="healths">
                <Grid key="title" item xs={6}>
                    <Typography variant="h6" style={{margin: 20, fontWeight: 'bold'}} gutterBottom>
                        Health Conditions
                    </Typography>
                </Grid>
                <Grid key="health-search" item xs={6}>
                    <TextField
                        id="search"
                        variant="outlined"
                        label="Search for health conditions"
                        value={searchUndebounced}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            width: '100%',
                            marginTop: 5,
                            marginBottom: 10,
                        }}
                    />
                </Grid>
                {Object.entries(filteredSections).map(([key, questions]) => {
                    const Component = healthConditionSections[key as keyof typeof healthConditionSections];
                    return (
                        <Component
                            key={key}
                            coreQuestions={questions}
                            counter={counterElement}
                            valueChanged={enhancedChanged}
                            parameters={parameters}
                        />
                    );
                })}
                <div style={{marginBottom: 15, width: '100%'}} />
            </Fragment>
        </>
    );
});

export default HealthQuestionsList;
